import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import { getAdminDashboard, getClinicDashboard } from "service/api";
import { Bar } from "react-chartjs-2";

// ── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, sub, icon, color, to }) => (
  <Card className="mb-3" style={{ borderLeft: `4px solid ${color}`, borderRadius: 10 }}>
    <CardBody className="d-flex align-items-center justify-content-between py-3 px-3">
      <div>
        <p className="text-muted mb-1" style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</p>
        <h3 className="mb-0 fw-bold" style={{ color }}>{value ?? 0}</h3>
        {sub !== undefined && <small className="text-muted">{sub}</small>}
      </div>
      <div style={{ width: 44, height: 44, borderRadius: "50%", background: color + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className={`mdi ${icon}`} style={{ fontSize: 22, color }}></i>
      </div>
    </CardBody>
    {to && (
      <div style={{ borderTop: "1px solid #f0f0f0", padding: "6px 16px" }}>
        <Link to={to} style={{ fontSize: 12, color, textDecoration: "none" }}>View all <i className="mdi mdi-arrow-right"></i></Link>
      </div>
    )}
  </Card>
);

// ── Section heading ───────────────────────────────────────────────────────────
const SectionHead = ({ icon, title }) => (
  <h6 style={{ fontWeight: 700, color: "#374151", borderBottom: "2px solid #e5e7eb", paddingBottom: 8, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
    <i className={`mdi ${icon}`} style={{ color: "#6366f1" }}></i> {title}
  </h6>
);

// ── Badge helper ─────────────────────────────────────────────────────────────
const StatusBadge = ({ value }) => {
  const map = { Approved: ["#16a34a", "#f0fdf4"], Pending: ["#d97706", "#fffbeb"], Cancelled: ["#dc2626", "#fef2f2"] };
  const [fg, bg] = map[value] || ["#6b7280", "#f9fafb"];
  return <span style={{ background: bg, color: fg, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{value || "—"}</span>;
};

// ── Skeleton loader ──────────────────────────────────────────────────────────
const Shimmer = ({ w = "100%", h = 14, r = 6, style = {} }) => (
  <div className="dash-skel" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const SkeletonStatCard = ({ color = "#e5e7eb" }) => (
  <Card className="mb-3" style={{ borderLeft: `4px solid ${color}`, borderRadius: 10 }}>
    <CardBody className="d-flex align-items-center justify-content-between py-3 px-3">
      <div style={{ flex: 1 }}>
        <Shimmer w={90} h={10} style={{ marginBottom: 10 }} />
        <Shimmer w={60} h={22} style={{ marginBottom: 8 }} />
        <Shimmer w={70} h={10} />
      </div>
      <Shimmer w={44} h={44} r={22} />
    </CardBody>
    <div style={{ borderTop: "1px solid #f0f0f0", padding: "6px 16px" }}>
      <Shimmer w={60} h={10} />
    </div>
  </Card>
);

const SkeletonRow = () => (
  <tr>
    {[...Array(5)].map((_, i) => (
      <td key={i}><Shimmer h={12} /></td>
    ))}
  </tr>
);

const DashboardSkeleton = () => (
  <div className="page-content">
    <style>{`
      @keyframes dashShimmer { 0% { background-position: -400px 0 } 100% { background-position: 400px 0 } }
      .dash-skel {
        background: linear-gradient(90deg, #eef0f3 0%, #f7f8fa 50%, #eef0f3 100%);
        background-size: 800px 100%;
        animation: dashShimmer 1.4s ease-in-out infinite;
        display: inline-block;
      }
    `}</style>
    <Container fluid>
      {/* Page title */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Shimmer w={220} h={22} style={{ marginBottom: 8 }} />
          <Shimmer w={160} h={12} />
        </div>
        <Shimmer w={90} h={26} r={20} />
      </div>

      {/* Top stat cards */}
      <Row>
        {["#6366f1", "#22c55e", "#f59e0b", "#0ea5e9"].map((c, i) => (
          <Col md={3} sm={6} key={i}><SkeletonStatCard color={c} /></Col>
        ))}
      </Row>

      {/* Secondary stat cards */}
      <Row>
        {["#8b5cf6", "#dc2626", "#d97706"].map((c, i) => (
          <Col md={4} sm={6} key={i}><SkeletonStatCard color={c} /></Col>
        ))}
      </Row>

      {/* Chart card */}
      <Row className="mb-4">
        <Col md={12}>
          <Card style={{ borderRadius: 12 }}>
            <CardBody>
              <Shimmer w={280} h={16} style={{ marginBottom: 18 }} />
              <Shimmer h={220} r={8} />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Table card */}
      <Row className="mb-4">
        <Col md={12}>
          <Card style={{ borderRadius: 12 }}>
            <CardBody>
              <Shimmer w={200} h={16} style={{ marginBottom: 18 }} />
              <table className="table table-sm" style={{ fontSize: 13 }}>
                <tbody>
                  {[...Array(4)].map((_, i) => <SkeletonRow key={i} />)}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  </div>
);

const Dashboard = (props) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  document.title = "Dashboard | Optom";

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem("token");
      let res;
      if (token) {
        const decoded = jwtDecode(token);
        const clinicID = decoded?.clinicID;
        if (clinicID) {
          res = await getClinicDashboard(clinicID);
          setIsAdmin(false);
        } else {
          res = await getAdminDashboard();
          setIsAdmin(true);
        }
        if (res?.data) setData(res.data);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const s = data?.stats || {};
  const charts = data?.charts || {};

  // ── Chart configs ──
  const barData = {
    labels: charts.labels || [],
    datasets: [
      {
        label: "Visits",
        data: charts.visits || [],
        backgroundColor: "rgba(99,102,241,0.75)",
      },
      {
        label: "New Patients",
        data: charts.patients || [],
        backgroundColor: "rgba(34,197,94,0.75)",
      },
    ],
  };


  const chartOptions = (yLabel) => ({
    responsive: true,
    maintainAspectRatio: true,
    legend: { position: "top", labels: { fontSize: 12, boxWidth: 12 } },
    tooltips: { mode: "index", intersect: false },
    scales: {
      xAxes: [{ gridLines: { display: false }, ticks: { fontSize: 11 } }],
      yAxes: [{ gridLines: { color: "#f0f0f0" }, ticks: { fontSize: 11 }, scaleLabel: { display: !!yLabel, labelString: yLabel } }],
    },
  });

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="page-content">
      <Container fluid>

        {/* ── Page title ── */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-0 fw-bold" style={{ color: "#1f2937" }}>
              {isAdmin ? "Admin Dashboard" : "Practice Dashboard"}
            </h4>
            <small className="text-muted">
              {new Date().toLocaleDateString("en-ZA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </small>
          </div>
          <span className={`badge ${isAdmin ? "bg-danger" : "bg-primary"}`} style={{ fontSize: 12, padding: "6px 14px" }}>
            {isAdmin ? "Admin" : "Practice User"}
          </span>
        </div>

        {/* ── STAT CARDS ── */}
        <Row>
          <Col md={3} sm={6}>
            <StatCard title="Total Patients" value={s.totalPatients} sub={`+${s.todayPatients ?? 0} today`} icon="mdi-account-group" color="#6366f1" to="/Patient" />
          </Col>
          <Col md={3} sm={6}>
            <StatCard title="Total Visits" value={s.totalVisits} sub={`+${s.todayVisits ?? 0} today`} icon="mdi-stethoscope" color="#22c55e" to="/visit" />
          </Col>
          <Col md={3} sm={6}>
            <StatCard title="Appointments" value={s.totalAppointments} sub={`${s.pendingAppointments ?? 0} pending`} icon="mdi-calendar-clock" color="#f59e0b" to="/AppointmentList" />
          </Col>
          <Col md={3} sm={6}>
            <StatCard title="Lab Orders" value={s.totalLabOrders} sub={`+${s.todayLabOrders ?? 0} today`} icon="mdi-flask-outline" color="#0ea5e9" to="/LabOrderList" />
          </Col>
        </Row>

        <Row>
          <Col md={4} sm={6}>
            <StatCard title="Total Invoices" value={s.totalInvoices} icon="mdi-receipt" color="#8b5cf6" to="/List/Invoice" />
          </Col>
          <Col md={4} sm={6}>
            <StatCard title="Today's Consultations" value={s.todayVisits ?? 0} icon="mdi-doctor" color="#dc2626" />
          </Col>
          <Col md={4} sm={6}>
            <StatCard title="Today's Appointments" value={s.todayAppointments ?? 0} icon="mdi-calendar-today" color="#d97706" />
          </Col>
        </Row>

        {/* ── CHARTS ── */}
        <Row className="mb-4">
          <Col md={12}>
            <Card style={{ borderRadius: 12 }}>
              <CardBody>
                <SectionHead icon="mdi-chart-bar" title="Monthly Visits & New Patients (Last 6 Months)" />
                <Bar data={barData} options={chartOptions()} height={80} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── TODAY'S APPOINTMENTS ── */}
        <Row className="mb-4">
          <Col md={12}>
            <Card style={{ borderRadius: 12 }}>
              <CardBody>
                <SectionHead icon="mdi-calendar-check" title="Today's Appointments" />
                {data?.todaysAppointmentList?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                      <thead style={{ background: "#f9fafb" }}>
                        <tr>
                          <th style={{ fontWeight: 600, color: "#374151" }}>#</th>
                          <th style={{ fontWeight: 600, color: "#374151" }}>Patient</th>
                          <th style={{ fontWeight: 600, color: "#374151" }}>Time</th>
                          <th style={{ fontWeight: 600, color: "#374151" }}>Type</th>
                          <th style={{ fontWeight: 600, color: "#374151" }}>Status</th>
                          {isAdmin && <th style={{ fontWeight: 600, color: "#374151" }}>Practice</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {data.todaysAppointmentList.map((a, i) => (
                          <tr key={a._id}>
                            <td className="text-muted">{i + 1}</td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{a.patient_name || "—"}</div>
                              <small className="text-muted">{a.patient_mpi || a.patient_idno || ""}</small>
                            </td>
                            <td>{a.appointment_time || "—"}</td>
                            <td><span style={{ fontSize: 12, color: "#6366f1" }}>{a.apnt_type || "—"}</span></td>
                            <td><StatusBadge value={a.appointment_status} /></td>
                            {isAdmin && <td><small className="text-muted">{a.clinic_name || "—"}</small></td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>No appointments scheduled for today.</p>
                )}
                <div className="text-end mt-1">
                  <Link to="/AppointmentList" style={{ fontSize: 12, color: "#6366f1" }}>View all appointments <i className="mdi mdi-arrow-right"></i></Link>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── RECENT PATIENTS + RECENT INVOICES ── */}
        <Row className="mb-4">
          <Col md={6}>
            <Card style={{ borderRadius: 12, height: "100%" }}>
              <CardBody>
                <SectionHead icon="mdi-account-multiple-plus" title="Recent Patients" />
                {data?.recentPatients?.length > 0 ? (
                  <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                    <thead style={{ background: "#f9fafb" }}>
                      <tr>
                        <th style={{ fontWeight: 600, color: "#374151" }}>Name</th>
                        <th style={{ fontWeight: 600, color: "#374151" }}>MPI</th>
                        <th style={{ fontWeight: 600, color: "#374151" }}>Mobile</th>
                        {isAdmin && <th style={{ fontWeight: 600, color: "#374151" }}>Practice</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentPatients.map((p) => (
                        <tr key={p._id}>
                          <td style={{ fontWeight: 500 }}>{[p.title, p.firstname, p.lastname].filter(Boolean).join(" ")}</td>
                          <td><span style={{ color: "#6366f1", fontWeight: 600 }}>{p.mpino || "—"}</span></td>
                          <td>{p.mobile || "—"}</td>
                          {isAdmin && <td><small className="text-muted">{p.clinicname?.name || "—"}</small></td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>No recent patients.</p>
                )}
                <div className="text-end mt-1">
                  <Link to="/Listpatient" style={{ fontSize: 12, color: "#6366f1" }}>View all <i className="mdi mdi-arrow-right"></i></Link>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={6}>
            <Card style={{ borderRadius: 12, height: "100%" }}>
              <CardBody>
                <SectionHead icon="mdi-receipt" title="Recent Invoices" />
                {data?.recentInvoices?.length > 0 ? (
                  <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                    <thead style={{ background: "#f9fafb" }}>
                      <tr>
                        <th style={{ fontWeight: 600, color: "#374151" }}>Invoice No</th>
                        <th style={{ fontWeight: 600, color: "#374151" }}>Patient</th>
                        <th style={{ fontWeight: 600, color: "#374151" }}>Type</th>
                        <th style={{ fontWeight: 600, color: "#374151" }} className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentInvoices.map((inv) => {
                        const pt = inv.patient_id;
                        const ptName = pt ? [pt.title, pt.firstname, pt.lastname].filter(Boolean).join(" ") : "—";
                        return (
                          <tr key={inv._id}>
                            <td><span style={{ color: "#16a34a", fontWeight: 700 }}>{inv.invoice_no}</span></td>
                            <td style={{ fontWeight: 500 }}>{ptName}</td>
                            <td>
                              <span className={`badge bg-${inv.invoice_type === "Medical Aid" ? "success" : inv.invoice_type === "Cash" ? "warning" : "info"}`} style={{ fontSize: 10 }}>
                                {inv.invoice_type || "—"}
                              </span>
                            </td>
                            <td className="text-end fw-bold" style={{ color: "#16a34a" }}>R {parseFloat(inv.total_amount || 0).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>No recent invoices.</p>
                )}
                <div className="text-end mt-1">
                  <Link to="/List/Invoice" style={{ fontSize: 12, color: "#6366f1" }}>View all <i className="mdi mdi-arrow-right"></i></Link>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── RECENT LAB ORDERS ── */}
        <Row className="mb-4">
          <Col md={12}>
            <Card style={{ borderRadius: 12 }}>
              <CardBody>
                <SectionHead icon="mdi-flask-outline" title="Recent Lab Orders" />
                {data?.recentLabOrders?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                      <thead style={{ background: "#f9fafb" }}>
                        <tr>
                          <th style={{ fontWeight: 600, color: "#374151" }}>Order No</th>
                          <th style={{ fontWeight: 600, color: "#374151" }}>VC Reference</th>
                          <th style={{ fontWeight: 600, color: "#374151" }}>Patient</th>
                          <th style={{ fontWeight: 600, color: "#374151" }}>Practice</th>
                          <th style={{ fontWeight: 600, color: "#374151" }}>Frame</th>
                          <th style={{ fontWeight: 600, color: "#374151" }} className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.recentLabOrders.map((lab) => (
                          <tr key={lab._id}>
                            <td><span style={{ color: "#0ea5e9", fontWeight: 700, fontFamily: "monospace" }}>{lab.order_no || "—"}</span></td>
                            <td><span style={{ color: "#dc2626", fontWeight: 600 }}>{lab.vc_reference || "—"}</span></td>
                            <td>{lab.patient_name || "—"}</td>
                            <td><small className="text-muted">{lab.practisc_name || "—"}</small></td>
                            <td>{[lab.frame_brand, lab.frame_color].filter(Boolean).join(" / ") || "—"}</td>
                            <td className="text-end fw-bold" style={{ color: "#16a34a" }}>R {parseFloat(lab.total || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>No recent lab orders.</p>
                )}
                <div className="text-end mt-1">
                  <Link to="/LabOrderList" style={{ fontSize: 12, color: "#6366f1" }}>View all <i className="mdi mdi-arrow-right"></i></Link>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── QUICK LINKS ── */}
        <Row className="mb-2">
          <Col md={12}>
            <Card style={{ borderRadius: 12, background: "#f8faff" }}>
              <CardBody>
                <SectionHead icon="mdi-lightning-bolt" title="Quick Actions" />
                <div className="d-flex flex-wrap gap-2">
                  {[
                    { to: "/Addpatient", icon: "mdi-account-plus", label: "New Patient", color: "#6366f1" },
                    { to: "/Addvisit", icon: "mdi-stethoscope", label: "New Visit", color: "#22c55e" },
                    { to: "/Appointment", icon: "mdi-calendar-plus", label: "New Appointment", color: "#f59e0b" },
                    { to: "/addJobform", icon: "mdi-briefcase-plus", label: "New Job", color: "#0ea5e9" },
                    { to: "/Addlab", icon: "mdi-flask-plus", label: "New Lab Order", color: "#8b5cf6" },
                    { to: "/Addpatientcare", icon: "mdi-clipboard-plus", label: "Patient Care", color: "#dc2626" },
                    { to: "/Invoice/Add", icon: "mdi-receipt-text-plus", label: "New Invoice", color: "#16a34a" },
                  ].map((q) => (
                    <Link key={q.to} to={q.to} style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: "#fff", border: `1.5px solid ${q.color}30`,
                      borderRadius: 8, padding: "8px 14px", textDecoration: "none",
                      color: q.color, fontWeight: 600, fontSize: 13,
                      transition: "background 0.2s",
                    }}>
                      <i className={`mdi ${q.icon}`} style={{ fontSize: 18 }}></i> {q.label}
                    </Link>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(Dashboard);
